// Tutorial interactivo del módulo Validación de Servicios TP.
//
// Combina dos piezas (A + B):
//  B) Modal de BIENVENIDA con slides explicativos (qué es la vista y qué se
//     puede hacer), como introducción conceptual.
//  A) TOUR GUIADO con react-joyride, anclado a elementos reales de la UI
//     mediante atributos `data-tour`, con tooltips paso a paso.
//
// Soporta dos variantes (prop `variant`):
//  - "list"   (por defecto): tour de la vista Lista (filtros, export, etc.).
//  - "detail": tour de la vista de Validación/Detalle de un servicio.
//
// Disparo MANUAL: el padre controla la visibilidad con `open` y lanza el flujo
// con el botón "Ver tutorial". Al cerrar el modal de bienvenida arranca el
// tour; al terminar/omitir el tour se cierra todo.
//
// i18n: todos los textos salen de `t(...)` (claves en locales es/en) con
// fallback en español.
import React, { useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Joyride, STATUS } from "react-joyride"
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button, Progress,
} from "reactstrap"

// Selectores de los elementos de la vista Lista (deben existir como data-tour
// en ServiceValidationList). Si alguno no está en el DOM, Joyride lo omite.
export const TOUR_TARGETS = {
  viewToggle: '[data-tour="sv-view-toggle"]',
  dateFilter: '[data-tour="sv-date-filter"]',
  exportBtn: '[data-tour="sv-export"]',
  filters: '[data-tour="sv-filters"]',
  health: '[data-tour="sv-health"]',
  search: '[data-tour="sv-search"]',
  table: '[data-tour="sv-table"]',
}

// Selectores de la vista de Validación/Detalle de un servicio.
export const TOUR_TARGETS_DETAIL = {
  actions: '[data-tour="svd-actions"]',
  map: '[data-tour="svd-map"]',
  tabs: '[data-tour="svd-tabs"]',
  rebuild: '[data-tour="svd-rebuild"]',
  scaleTools: '[data-tour="svd-scale-tools"]',
  editScales: '[data-tour="svd-edit-scales"]',
  notes: '[data-tour="svd-notes"]',
  audit: '[data-tour="svd-audit"]',
  // PT-4070 (FEAT-003): flujos nuevos del detalle de validación. El stepper de
  // 2 pasos se eliminó: el flujo de montos (Calcular/Editar/Validar) vive ahora
  // en el panel lateral "Montos del servicio" (anclas calcAmounts/validateCta).
  closeNoCustomer: '[data-tour="svd-close-no-customer"]',
  calcAmounts: '[data-tour="svd-calc-amounts"]',
  checks: '[data-tour="svd-checks"]',
  validateCta: '[data-tour="svd-validate-cta"]',
}

// Selectores del Dashboard de KPIs de validación.
export const TOUR_TARGETS_DASHBOARD = {
  dateFilter: '[data-tour="svdash-date"]',
  userFilter: '[data-tour="svdash-user"]',
  export: '[data-tour="svdash-export"]',
  kpis: '[data-tour="svdash-kpis"]',
  period: '[data-tour="svdash-period"]',
  byHour: '[data-tour="svdash-byhour"]',
  byUser: '[data-tour="svdash-byuser"]',
}

// Slides del modal de bienvenida (B) de la vista LISTA.
const listSlides = (t) => [
  {
    icon: "bx-info-circle",
    title: t("TourWelcomeTitle") || "Validación de Servicios TP",
    body: t("TourWelcomeBody")
      || "Este módulo te permite revisar, filtrar y validar los servicios de transporte de personal. Veamos lo esencial en unos pasos.",
  },
  {
    icon: "bx-slider-alt",
    title: t("TourSlideFiltersTitle") || "Filtra y encuentra servicios",
    body: t("TourSlideFiltersBody")
      || "Filtra por empresa, código, placa, plaza, estatus, validación y salud de ruta. Los filtros se comparten entre las vistas Lista y Kanban.",
  },
  {
    icon: "bx-pulse",
    title: t("TourSlideHealthTitle") || "Salud de ruta (GRS)",
    body: t("TourSlideHealthBody")
      || "Cada servicio muestra un indicador de salud de ruta (de crítica a excelente) calculado por GRS, consistente en lista, kanban y detalle.",
  },
  {
    icon: "bx-export",
    title: t("TourSlideExportTitle") || "Exporta la información",
    body: t("TourSlideExportBody")
      || "Descarga la lista filtrada a Excel (generado en el backend). En el Dashboard también puedes exportar el reporte a Excel y las gráficas a PDF.",
  },
]

// Slides del modal de bienvenida (B) de la vista DETALLE/VALIDACIÓN.
const detailSlides = (t) => [
  {
    icon: "bx-detail",
    title: t("TourDetailWelcomeTitle") || "Validar un servicio",
    body: t("TourDetailWelcomeBody")
      || "En esta vista revisas el detalle de un servicio y decides si lo validas. Veamos las acciones y secciones principales.",
  },
  {
    icon: "bx-map-alt",
    title: t("TourDetailMapTitle") || "Recorrido en el mapa",
    body: t("TourDetailMapBody")
      || "El mapa muestra las capas del recorrido (original, sanitizada y reconstruida) con replay. Puedes ampliarlo y enfocar origen, destino o una escala.",
  },
  {
    icon: "bx-git-repo-forked",
    title: t("TourDetailRebuildTitle") || "Escalas y reconstrucción",
    body: t("TourDetailRebuildBody")
      || "Edita las escalas (origen, destino, kms y duración) y reconstruye el recorrido con GRS. Los montos se recalculan al pulsar 'Calcular montos' en el panel 'Montos del servicio'.",
  },
  {
    icon: "bx-check-shield",
    title: t("TourDetailValidateTitle") || "Validar o invalidar",
    body: t("TourDetailValidateBody")
      || "Cuando la información es correcta, valida el servicio. También puedes invalidarlo, comparar versiones (diff) y pasar al siguiente por validar.",
  },
]

// Slides del modal de bienvenida (B) del DASHBOARD de KPIs.
const dashboardSlides = (t) => [
  {
    icon: "bx-bar-chart-alt-2",
    title: t("TourDashWelcomeTitle") || "Dashboard de validación",
    body: t("TourDashWelcomeBody")
      || "Mide la validación de servicios TP: automáticas vs manuales, editadas, totales y productividad por usuario y por hora. Veamos lo esencial.",
  },
  {
    icon: "bx-filter-alt",
    title: t("TourDashFiltersTitle") || "Filtra por fecha y usuario",
    body: t("TourDashFiltersBody")
      || "Acota por rango de fecha y por un validador concreto. Todos los indicadores y gráficas se recalculan con esos filtros.",
  },
  {
    icon: "bx-bar-chart",
    title: t("TourDashPeriodTitle") || "Período interactivo",
    body: t("TourDashPeriodBody")
      || "Haz clic en una columna del gráfico de período para ver el desglose por hora del día de ese período concreto.",
  },
  {
    icon: "bx-export",
    title: t("TourDashExportTitle") || "Exporta el reporte",
    body: t("TourDashExportBody")
      || "Descarga el reporte a Excel (generado en el backend) o captura los indicadores y gráficas a PDF.",
  },
]

const ServiceValidationTour = ({ t, open, onClose, variant = "list" }) => {
  const isDetail = variant === "detail"
  const isDashboard = variant === "dashboard"
  // phase: "welcome" (modal B) -> "tour" (joyride A) -> cerrado.
  const [phase, setPhase] = useState("welcome")
  const [slide, setSlide] = useState(0)
  const slides = useMemo(
    () => (isDashboard ? dashboardSlides(t) : isDetail ? detailSlides(t) : listSlides(t)),
    [isDashboard, isDetail, t],
  )

  // Reinicia el estado interno cada vez que se abre desde el padre.
  React.useEffect(() => {
    if (open) {
      setPhase("welcome")
      setSlide(0)
    }
  }, [open])

  // Pasos del tour (A). `disableBeacon` arranca el paso sin el punto pulsante.
  const listSteps = useMemo(() => ([
    {
      target: TOUR_TARGETS.viewToggle,
      title: t("TourStepViewTitle") || "Lista y Kanban",
      content: t("TourStepViewBody")
        || "Alterna entre la vista Lista y el tablero Kanban. Tus filtros se conservan al cambiar de vista.",
      disableBeacon: true,
    },
    {
      target: TOUR_TARGETS.dateFilter,
      title: t("TourStepDateTitle") || "Rango de fecha",
      content: t("TourStepDateBody")
        || "Elige el rango de fecha (Hoy por defecto). Se aplica de inmediato al seleccionarlo.",
    },
    {
      target: TOUR_TARGETS.exportBtn,
      title: t("TourStepExportTitle") || "Exportar a Excel",
      content: t("TourStepExportBody")
        || "Descarga a Excel todos los servicios que coinciden con tus filtros. Se habilita solo cuando hay resultados.",
    },
    {
      target: TOUR_TARGETS.filters,
      title: t("TourStepFiltersTitle") || "Filtros",
      content: t("TourStepFiltersBody")
        || "Combina empresa, código, placa, plaza, estatus y validación. Pulsa Buscar para aplicarlos.",
    },
    {
      target: TOUR_TARGETS.health,
      title: t("TourStepHealthTitle") || "Filtro de salud de ruta",
      content: t("TourStepHealthBody")
        || "Filtra por nivel de salud de ruta: crítica, baja, moderada, buena o excelente.",
    },
    {
      target: TOUR_TARGETS.search,
      title: t("TourStepSearchTitle") || "Aplicar filtros",
      content: t("TourStepSearchBody")
        || "Aplica los filtros del panel, límpialos o recarga los resultados.",
    },
    {
      target: TOUR_TARGETS.table,
      title: t("TourStepTableTitle") || "Resultados",
      content: t("TourStepTableBody")
        || "Ordena por columnas, revisa la salud de ruta y haz clic en un servicio para abrir su detalle.",
    },
  ]), [t])

  // Pasos del tour de la vista DETALLE/VALIDACIÓN. Los targets opcionales
  // (reconstrucción, editar, bitácora) solo existen según permisos/estado; si no
  // están en el DOM, Joyride los omite sin romper el recorrido.
  const detailSteps = useMemo(() => ([
    {
      target: TOUR_TARGETS_DETAIL.actions,
      title: t("TourDetailStepActionsTitle") || "Acciones del servicio",
      content: t("TourDetailStepActionsBody")
        || "Desde aquí validas o invalidas el servicio, comparas versiones (diff) y navegas al anterior/siguiente por validar. En el menú de opciones también puedes 'Validar en 0s' (validar con montos y kms en cero).",
      disableBeacon: true,
    },
    {
      target: TOUR_TARGETS_DETAIL.map,
      title: t("TourDetailStepMapTitle") || "Mapa del recorrido",
      content: t("TourDetailStepMapBody")
        || "Visualiza las capas del recorrido con replay y amplía el mapa. Haz clic en origen/destino o una escala para enfocarlos.",
    },
    {
      target: TOUR_TARGETS_DETAIL.tabs,
      title: t("TourDetailStepTabsTitle") || "Escalas, evidencias, pasajeros y timeline",
      content: t("TourDetailStepTabsBody")
        || "Cambia entre las pestañas para revisar las escalas, las evidencias (PODs), los pasajeros y la línea de tiempo del viaje.",
    },
    {
      target: TOUR_TARGETS_DETAIL.scaleTools,
      title: t("TourDetailStepScaleToolsTitle") || "Herramientas de escalas",
      content: t("TourDetailStepScaleToolsBody")
        || "En este menú tienes 'Actualizar direcciones' (vuelve a geocodificar las direcciones de las escalas a partir de sus coordenadas, corrige direcciones invertidas) y 'Reconstrucción de ruta' (recalcula el recorrido con GRS a partir de las escalas guardadas).",
    },
    {
      target: TOUR_TARGETS_DETAIL.editScales,
      title: t("TourDetailStepEditTitle") || "Editar escalas",
      content: t("TourDetailStepEditBody")
        || "Edita origen, destino, kms y duración de cada escala, reordénalas o agrégalas. Al guardar se recalculan montos; opcionalmente planeas la ruta.",
    },
    {
      target: TOUR_TARGETS_DETAIL.notes,
      title: t("TourDetailStepNotesTitle") || "Notas",
      content: t("TourDetailStepNotesBody")
        || "Agrega notas al servicio mientras no esté validado. Quedan registradas en la bitácora.",
    },
    {
      target: TOUR_TARGETS_DETAIL.closeNoCustomer,
      title: t("TourDetailStepCloseNoCustomerTitle") || "Cierre sin cliente",
      content: t("TourDetailStepCloseNoCustomerBody")
        || "Si el servicio se cierra sin cliente, usa este botón. Se abre un overlay para elegir el tipo de cierre (en zona, fuera de zona o foráneo) y confirmar.",
    },
    {
      target: TOUR_TARGETS_DETAIL.calcAmounts,
      title: t("TourDetailStepCalcAmountsTitle") || "Calcular y editar montos",
      content: t("TourDetailStepCalcAmountsBody")
        || "En el panel 'Montos del servicio' pulsa Calcular montos para que el backend recalcule. Tras calcular aparece el botón Editar para ajustar el monto proveedor; la edición queda registrada en la bitácora.",
    },
    {
      target: TOUR_TARGETS_DETAIL.checks,
      title: t("TourDetailStepChecksTitle") || "Indicadores del servicio",
      content: t("TourDetailStepChecksBody")
        || "Los indicadores se organizan en dos columnas: a la izquierda cierre sin cliente, ruta planificada y documentación pendiente; a la derecha validación automática, validado y corregido manualmente.",
    },
    {
      target: TOUR_TARGETS_DETAIL.validateCta,
      title: t("TourDetailStepConfirmTitle") || "Confirmar la validación",
      content: t("TourDetailStepConfirmBody")
        || "Al pulsar Validar se abre un overlay de confirmación con opciones aceptar o cancelar, para evitar validaciones accidentales.",
    },
    {
      target: TOUR_TARGETS_DETAIL.audit,
      title: t("TourDetailStepAuditTitle") || "Bitácora",
      content: t("TourDetailStepAuditBody")
        || "Consulta el historial de cambios y versiones del servicio (quién, qué y cuándo).",
    },
    {
      target: TOUR_TARGETS_DETAIL.audit,
      title: t("TourDetailStepBreakdownTitle") || "Desglose de montos en la bitácora",
      content: t("TourDetailStepBreakdownBody")
        || "La bitácora muestra primero el desglose aritmético de montos: la edición manual y las operaciones por componente (precio por km, por tiempo, evidencias) para proveedor y cliente.",
    },
  ]), [t])

  // Pasos del tour del DASHBOARD. Targets anclados a data-tour del dashboard;
  // si alguno no está en el DOM (p. ej. sin datos), Joyride lo omite.
  const dashboardSteps = useMemo(() => ([
    {
      target: TOUR_TARGETS_DASHBOARD.dateFilter,
      title: t("TourDashStepDateTitle") || "Rango de fecha",
      content: t("TourDashStepDateBody")
        || "Elige el período a analizar. Todos los indicadores y gráficas se recalculan al cambiarlo.",
      disableBeacon: true,
    },
    {
      target: TOUR_TARGETS_DASHBOARD.userFilter,
      title: t("TourDashStepUserTitle") || "Filtro de usuario",
      content: t("TourDashStepUserBody")
        || "Filtra los indicadores por un validador concreto. La lista de usuarios se carga una vez y se cachea.",
    },
    {
      target: TOUR_TARGETS_DASHBOARD.kpis,
      title: t("TourDashStepKpisTitle") || "Indicadores",
      content: t("TourDashStepKpisBody")
        || "Validaciones automáticas, manuales, editadas y el total del período filtrado.",
    },
    {
      target: TOUR_TARGETS_DASHBOARD.period,
      title: t("TourDashStepPeriodTitle") || "Validados por período",
      content: t("TourDashStepPeriodBody")
        || "Haz clic en una columna para desglosar las validaciones por hora del día de ese período.",
    },
    {
      target: TOUR_TARGETS_DASHBOARD.byHour,
      title: t("TourDashStepByHourTitle") || "Validados por hora",
      content: t("TourDashStepByHourBody")
        || "Throughput por hora del día (0-23). Al seleccionar un período arriba, muestra solo ese período.",
    },
    {
      target: TOUR_TARGETS_DASHBOARD.byUser,
      title: t("TourDashStepByUserTitle") || "Detalle por usuario",
      content: t("TourDashStepByUserBody")
        || "Validaciones y tiempo promedio por usuario y período. Cambia la granularidad entre día, semana y mes.",
    },
    {
      target: TOUR_TARGETS_DASHBOARD.export,
      title: t("TourDashStepExportTitle") || "Exportar",
      content: t("TourDashStepExportBody")
        || "Descarga el reporte a Excel (backend) o los indicadores y gráficas a PDF.",
    },
  ]), [t])

  const steps = isDashboard ? dashboardSteps : isDetail ? detailSteps : listSteps

  if (!open) return null

  // --- Fase A: tour guiado ---
  if (phase === "tour") {
    return (
      <Joyride
        steps={steps}
        run
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep={false}
        scrollOffset={120}
        disableScrollParentFix
        locale={{
          back: t("TourBack") || "Anterior",
          close: t("Close") || "Cerrar",
          last: t("TourFinish") || "Finalizar",
          next: t("TourNext") || "Siguiente",
          skip: t("TourSkip") || "Omitir",
        }}
        floaterProps={{
          disableAnimation: false,
          styles: { floater: { fontFamily: "'Poppins', sans-serif" } },
        }}
        styles={{
          options: {
            primaryColor: "#FF5F00",
            textColor: "#495057",
            arrowColor: "#FFFFFF",
            backgroundColor: "#FFFFFF",
            overlayColor: "rgba(33, 37, 41, 0.55)",
            zIndex: 11000,
          },
          tooltip: {
            fontFamily: "'Poppins', sans-serif",
            borderRadius: 10,
            padding: 16,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18)",
          },
          tooltipTitle: {
            fontSize: 15,
            fontWeight: 600,
            color: "#212529",
            marginBottom: 6,
          },
          tooltipContent: {
            fontSize: 13,
            lineHeight: 1.5,
            padding: "4px 0 0",
          },
          buttonNext: {
            fontFamily: "'Poppins', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            borderRadius: 6,
            padding: "8px 16px",
            background: "linear-gradient(135deg, #FF5F00, #FF8C00)",
            color: "#FFFFFF",
            outline: "none",
          },
          buttonBack: {
            fontFamily: "'Poppins', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            color: "#6C757D",
            marginRight: 8,
          },
          buttonSkip: {
            fontFamily: "'Poppins', sans-serif",
            fontSize: 13,
            color: "#ADB5BD",
          },
          beaconInner: {
            backgroundColor: "#FF5F00",
          },
          beaconOuter: {
            backgroundColor: "rgba(255, 95, 0, 0.2)",
            borderColor: "#FF5F00",
          },
        }}
        callback={(data) => {
          const { status } = data
          if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            onClose?.()
          }
        }}
      />
    )
  }

  // --- Fase B: modal de bienvenida con slides ---
  const isLast = slide === slides.length - 1
  const current = slides[slide]
  const pct = Math.round(((slide + 1) / slides.length) * 100)

  const next = () => {
    if (isLast) {
      // Terminó la intro: arranca el tour guiado sobre la UI real.
      setPhase("tour")
    } else {
      setSlide((s) => s + 1)
    }
  }
  const prev = () => setSlide((s) => Math.max(0, s - 1))

  return (
    <Modal isOpen toggle={onClose} centered>
      <ModalHeader>
        <i className="bx bx-book-open me-2" />
        {isDashboard
          ? (t("TourDashTitle") || "Tutorial: Dashboard de validación")
          : isDetail
            ? (t("TourDetailTitle") || "Tutorial: Validar un servicio")
            : (t("TourTitle") || "Tutorial: Validación de Servicios")}
      </ModalHeader>
      <ModalBody>
        <div className="text-center mb-3">
          <div className="sv-tour-icon mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle">
            <i className={`bx ${current.icon} fs-1`} />
          </div>
          <h5 className="mb-2">{current.title}</h5>
          <p className="text-muted mb-0">{current.body}</p>
        </div>
        <Progress value={pct} className="sv-tour-progress mb-1" style={{ height: 4 }} />
        <div className="text-muted small text-center">
          {slide + 1} / {slides.length}
        </div>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        <Button color="link" className="text-muted" onClick={onClose}>
          {t("TourSkip") || "Omitir"}
        </Button>
        <div className="d-flex gap-2">
          <Button color="light" onClick={prev} disabled={slide === 0}>
            {t("TourBack") || "Anterior"}
          </Button>
          <Button color="primary" className="sv-search-btn" onClick={next}>
            {isLast ? (t("TourStart") || "Iniciar recorrido") : (t("TourNext") || "Siguiente")}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

ServiceValidationTour.propTypes = {
  t: PropTypes.func.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  variant: PropTypes.oneOf(["list", "detail", "dashboard"]),
}

export default ServiceValidationTour
